import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Globe, 
  Building2, 
  Mail, 
  Users, 
  CreditCard,
  ArrowLeftRight,
  Gift
} from 'lucide-react';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About ParachPay | ASELU Investment Growth Limited</title>
        <meta name="description" content="ParachPay is operated by ASELU Investment Growth Limited, a company registered in Nigeria (RC1947196). Global payments platform for 64+ currencies." />
        <link rel="canonical" href="https://parachpay.com/about" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-emerald-500/5">
        {/* Header */}
        <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-xl font-bold font-heading">
              <img 
                src="/images/parachpay-logo.jpg" 
                alt="ParachPay Logo" 
                width="32"
                height="32"
                className="h-8 w-auto rounded-lg"
              />
              ParachPay
            </Link>
            <Link to="/">
              <Button variant="ghost" className="rounded-xl">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            {/* Page Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4">
                About <span className="text-emerald-500">ParachPay</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A global payments platform for sending, receiving, exchanging and managing money across 64+ currencies.
              </p>
            </motion.div>

            {/* Company Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="mb-8">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Building2 className="w-6 h-6 text-emerald-500" />
                    <h2 className="text-2xl font-semibold font-heading">Company Information</h2>
                  </div>
                  
                  <div className="prose prose-slate dark:prose-invert max-w-none">
                    <p className="text-muted-foreground leading-relaxed">
                      ParachPay is operated by <strong>ASELU Investment Growth Limited</strong>, a company registered in Nigeria with the Corporate Affairs Commission (<strong>RC1947196</strong>). ParachPay provides a global payments platform for sending, receiving, exchanging and managing money across 64+ currencies, with a referral program and merchant tools.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mt-8">
                    {/* Registered Office */}
                    <div className="p-4 rounded-xl bg-muted/50">
                      <div className="flex items-center gap-2 mb-3">
                        <Globe className="w-5 h-5 text-emerald-500" />
                        <h3 className="font-semibold">Registered office</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        67 Siluko Road,<br />
                        Benin City, Nigeria
                      </p>
                    </div>

                    {/* European Operations */}
                    <div className="p-4 rounded-xl bg-muted/50">
                      <div className="flex items-center gap-2 mb-3">
                        <Globe className="w-5 h-5 text-emerald-500" />
                        <h3 className="font-semibold">European operations</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Rudolf Steinerstraat 32,<br />
                        2037 GC Haarlem, Netherlands
                      </p>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="mt-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-emerald-500" />
                      <span className="font-medium">Contact:</span>
                      <a 
                        href="mailto:info@parachpay.com" 
                        className="text-emerald-600 dark:text-emerald-400 hover:underline"
                      >
                        info@parachpay.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* What We Offer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-semibold font-heading mb-6">What We Offer</h2>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                      <ArrowLeftRight className="w-5 h-5 text-emerald-500 mt-0.5" />
                      <div>
                        <h3 className="font-medium mb-1">Send & Receive</h3>
                        <p className="text-sm text-muted-foreground">
                          Transfer money globally across 64+ currencies
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                      <CreditCard className="w-5 h-5 text-emerald-500 mt-0.5" />
                      <div>
                        <h3 className="font-medium mb-1">Virtual Cards</h3>
                        <p className="text-sm text-muted-foreground">
                          Create virtual cards for online payments
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                      <Users className="w-5 h-5 text-emerald-500 mt-0.5" />
                      <div>
                        <h3 className="font-medium mb-1">Merchant Tools</h3>
                        <p className="text-sm text-muted-foreground">
                          Accept payments and manage business transactions
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/30">
                      <Gift className="w-5 h-5 text-emerald-500 mt-0.5" />
                      <div>
                        <h3 className="font-medium mb-1">Referral Program</h3>
                        <p className="text-sm text-muted-foreground">
                          Earn rewards by inviting friends and family
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-12 text-center"
            >
              <Link to="/register">
                <Button size="lg" className="rounded-xl bg-emerald-600 hover:bg-emerald-700">
                  Get Started with ParachPay
                </Button>
              </Link>
            </motion.div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t mt-16 py-8">
          <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} ParachPay. All rights reserved.</p>
            <p className="mt-2">ASELU Investment Growth Limited · Registered in Nigeria · RC1947196</p>
            <p className="mt-2">
              <a href="mailto:info@parachpay.com" className="text-emerald-500 hover:underline">info@parachpay.com</a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default About;
